// ─────────────────────────────────────────────────────────────
// API client — all endpoint calls live here.
// Base URL resolved from VITE_API_BASE_URL env var.
// Defaults to empty string so Vite's dev proxy (/api → backend) works.
// ─────────────────────────────────────────────────────────────

import type {
  BacklogLandscape,
  CardCreatePayload,
  CardHistoryEntry,
  CardUpdatePayload,
  CardsListResponse,
  DashboardMeta,
  FeatureCard,
  JamieChatRequest,
  JamieChatResponse,
  KanbanBoard,
  Sprint,
  SprintHistoryEntry,
  SprintWithItems,
  UpstreamError,
} from "../types";

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

// ─── Maintenance mode plumbing ────────────────────────────────
//
// Backend contract (Sam, 2026-05-06): when site-wide maintenance is on,
// any blocked endpoint returns HTTP 503 with a body of the form
//   { detail: string, maintenance: true, message: string|null, updated_at: ISO|null }
// (admin users with is_admin=true on their JWT bypass the gate entirely
// and should never see a 503-with-maintenance reply).
//
// We surface that to the app via a module-level handler the app installs
// at boot. The fetch wrapper invokes it on 503-with-maintenance and then
// throws a typed `MaintenanceError` so the caller can short-circuit too.

export interface MaintenanceErrorPayload {
  message: string | null;
  updated_at: string | null;
}

export class MaintenanceError extends Error {
  readonly maintenance = true;
  readonly payload: MaintenanceErrorPayload;
  constructor(payload: MaintenanceErrorPayload) {
    super(payload.message ?? "Site is under maintenance");
    this.name = "MaintenanceError";
    this.payload = payload;
  }
}

type MaintenanceHandler = (payload: MaintenanceErrorPayload) => void;

let maintenanceHandler: MaintenanceHandler | null = null;

/**
 * Register a global handler invoked whenever the API receives a
 * 503-with-maintenance response. The app calls this once at boot
 * (typically from the root component) so the maintenance state can
 * be flipped reactively on any in-flight request.
 *
 * Pass `null` to deregister (e.g. on root unmount during tests).
 */
export function setMaintenanceHandler(fn: MaintenanceHandler | null): void {
  maintenanceHandler = fn;
}

/**
 * Inspect a fetch Response. If it's a maintenance 503, dispatch the
 * registered handler and throw a `MaintenanceError`. Otherwise, return
 * `false` so the caller can continue with its normal error handling.
 *
 * The body is consumed only when the response truly is a maintenance
 * 503 — for all other 503s (or non-503s) we leave the body untouched
 * so the caller can still read it.
 */
async function handleMaintenanceResponse(res: Response): Promise<boolean> {
  if (res.status !== 503) return false;
  let body: unknown = null;
  try {
    body = await res.clone().json();
  } catch {
    return false;
  }
  if (
    body &&
    typeof body === "object" &&
    (body as { maintenance?: unknown }).maintenance === true
  ) {
    const payload: MaintenanceErrorPayload = {
      message:
        typeof (body as { message?: unknown }).message === "string"
          ? ((body as { message: string }).message)
          : null,
      updated_at:
        typeof (body as { updated_at?: unknown }).updated_at === "string"
          ? ((body as { updated_at: string }).updated_at)
          : null,
    };
    if (maintenanceHandler) {
      try {
        maintenanceHandler(payload);
      } catch {
        // Don't let a buggy handler swallow the actual error path.
      }
    }
    throw new MaintenanceError(payload);
  }
  return false;
}

// ─── Generic fetch wrapper ────────────────────────────────────

async function request<T>(
  path: string,
  options: RequestInit = {},
  headers: Record<string, string> = {}
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      ...(!(options.body instanceof FormData) && { "Content-Type": "application/json" }),
      ...headers,
      ...options.headers,
    },
  });

  // Maintenance 503 — dispatch handler + throw MaintenanceError.
  await handleMaintenanceResponse(res);

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      // Sam (2026-05-14) — basic-tier 403/429 carry a structured detail
      // object. Surface those as a typed TierLimitError so callers
      // (executeApi.run, etc.) can narrow on `err.detail.code` to drive
      // UX state. Falls back to the original string-detail handling for
      // every other HTTPException in the codebase.
      const tierLimit = tierLimitErrorFromBody(body);
      if (tierLimit) {
        throw tierLimit;
      }
      const b = body as { detail?: string; error?: string };
      message = b.detail ?? b.error ?? message;
    } catch (err) {
      if (err instanceof TierLimitError) throw err;
      // ignore JSON parse errors
    }
    throw new Error(message);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

function get<T>(path: string, headers?: Record<string, string>): Promise<T> {
  return request<T>(path, { method: "GET" }, headers);
}

function post<T>(
  path: string,
  body?: unknown,
  headers?: Record<string, string>
): Promise<T> {
  return request<T>(
    path,
    { method: "POST", body: body !== undefined ? JSON.stringify(body) : undefined },
    headers
  );
}

function put<T>(
  path: string,
  body?: unknown,
  headers?: Record<string, string>
): Promise<T> {
  return request<T>(
    path,
    { method: "PUT", body: body !== undefined ? JSON.stringify(body) : undefined },
    headers
  );
}

function patch<T>(
  path: string,
  body?: unknown,
  headers?: Record<string, string>
): Promise<T> {
  return request<T>(
    path,
    { method: "PATCH", body: body !== undefined ? JSON.stringify(body) : undefined },
    headers
  );
}

function del(path: string, headers?: Record<string, string>): Promise<void> {
  return request<void>(path, { method: "DELETE" }, headers);
}

// ─── Feature Cards ────────────────────────────────────────────

export interface CardListParams {
  status?: string;
  kanban_column?: string;
  tag?: string;
  source?: string;
  strategic_alignment?: string;
  search?: string;
  sort_by?: "priority" | "value_score" | "created_at" | "customer_request_count";
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

export const cardsApi = {
  list: (params: CardListParams = {}): Promise<CardsListResponse> =>
    get(`/api/dashboard/cards${buildQuery(params as Record<string, string | number | undefined>)}`),

  get: (id: number): Promise<FeatureCard> =>
    get(`/api/dashboard/cards/${id}`),

  create: (payload: CardCreatePayload): Promise<FeatureCard> =>
    post("/api/dashboard/cards", payload),

  update: (
    id: number,
    payload: CardUpdatePayload,
    changedBy?: string
  ): Promise<FeatureCard> =>
    put(
      `/api/dashboard/cards/${id}`,
      payload,
      changedBy ? { "X-Changed-By": changedBy } : {}
    ),

  delete: (id: number): Promise<void> =>
    del(`/api/dashboard/cards/${id}`),

  upvote: (id: number): Promise<{ id: number; upvotes: number }> =>
    post(`/api/dashboard/cards/${id}/upvote`),

  move: (
    id: number,
    column: string,
    sprintId?: number
  ): Promise<FeatureCard> =>
    post(`/api/dashboard/cards/${id}/move`, {
      kanban_column: column,
      ...(sprintId !== undefined && { sprint_id: sprintId }),
    }),

  history: (id: number): Promise<CardHistoryEntry[]> =>
    get(`/api/dashboard/cards/${id}/history`),
};

// ─── Sprints ──────────────────────────────────────────────────

export const sprintsApi = {
  list: (status?: string): Promise<Sprint[]> =>
    get(`/api/dashboard/sprints${status ? `?status=${status}` : ""}`),

  get: (id: number): Promise<SprintWithItems> =>
    get(`/api/dashboard/sprints/${id}`),

  create: (payload: {
    name: string;
    description?: string | null;
  }): Promise<Sprint> => post("/api/dashboard/sprints", payload),

  update: (
    id: number,
    payload: {
      name?: string;
      description?: string | null;
    }
  ): Promise<Sprint> => put(`/api/dashboard/sprints/${id}`, payload),

  // Sprint queue transitions (start/unqueue/confirm/complete) used to be
  // gated behind X-Admin-Password — every click in HistoryView/KanbanView
  // popped an admin-password dialog. Sam's 2026-05-15 follow-up reworked
  // `verify_admin` on the backend to accept EITHER an admin JWT OR the
  // password header, and Greg's policy now is: "an admin JWT is sufficient
  // for non-destructive admin actions everywhere". The frontend therefore
  // sends only the JWT via authHeader(); the password prompts are gone.
  // The Danger Zone (Reset User Data / Fresh Deploy) is the explicit
  // carve-out that will still require heightened confirmation when wired.
  start: (id: number): Promise<Sprint> =>
    post(`/api/dashboard/sprints/${id}/start`, undefined, authHeader()),

  unqueue: (id: number): Promise<Sprint> =>
    post(`/api/dashboard/sprints/${id}/unqueue`, undefined, authHeader()),

  confirm: (id: number): Promise<Sprint> =>
    post(`/api/dashboard/sprints/${id}/confirm`, undefined, authHeader()),

  complete: (id: number, summary?: string): Promise<Sprint> =>
    post(
      `/api/dashboard/sprints/${id}/complete`,
      { summary: summary ?? null },
      authHeader()
    ),

  addItem: (
    sprintId: number,
    payload: {
      card_id: number;
      kanban_column?: string;
      priority_in_sprint?: number;
      agent_assignment?: string | null;
    }
  ): Promise<unknown> => post(`/api/dashboard/sprints/${sprintId}/items`, payload),

  updateItem: (
    sprintId: number,
    itemId: number,
    payload: {
      kanban_column?: string;
      priority_in_sprint?: number;
      agent_assignment?: string | null;
    },
    changedBy?: string
  ): Promise<unknown> =>
    put(
      `/api/dashboard/sprints/${sprintId}/items/${itemId}`,
      payload,
      changedBy ? { "X-Changed-By": changedBy } : {}
    ),

  removeItem: (sprintId: number, itemId: number): Promise<void> =>
    del(`/api/dashboard/sprints/${sprintId}/items/${itemId}`),
};

// ─── Kanban ───────────────────────────────────────────────────

export const kanbanApi = {
  board: (sprintId?: number): Promise<KanbanBoard> =>
    get(
      `/api/dashboard/kanban${sprintId !== undefined ? `?sprint_id=${sprintId}` : ""}`
    ),
};

// ─── Backlog ──────────────────────────────────────────────────

export const backlogApi = {
  landscape: (): Promise<BacklogLandscape> => get("/api/dashboard/backlog"),
};

// ─── Sprint History ───────────────────────────────────────────

export const sprintHistoryApi = {
  list: (): Promise<SprintHistoryEntry[]> =>
    get("/api/dashboard/sprint-history"),

  get: (sprintId: number): Promise<SprintHistoryEntry> =>
    get(`/api/dashboard/sprint-history/${sprintId}`),
};

// ─── Jamie Chat ───────────────────────────────────────────────

export interface JamieChatStreamCallbacks {
  onToken: (token: string) => void;
  onDone: (action: JamieChatResponse["action"], cleanText: string) => void;
  onError: (message: string) => void;
  /**
   * Optional. Fired when the SSE stream delivers a structured upstream
   * error frame (Sam, 2026-05-14). Mirror of Marie's onUpstreamError —
   * see MarieChatStreamCallbacks for the contract. `onError` is ALSO
   * invoked with `error.message` for legacy listeners.
   */
  onUpstreamError?: (err: UpstreamError) => void;
}

export const jamieApi = {
  /**
   * Streams the Jamie chat response via SSE.
   * Calls onToken for each arriving token, onDone when the stream completes
   * (with the optional action payload), and onError if anything goes wrong.
   * Returns an AbortController so the caller can cancel mid-stream.
   */
  chatStream: (
    payload: JamieChatRequest,
    callbacks: JamieChatStreamCallbacks
  ): AbortController => {
    const controller = new AbortController();

    (async () => {
      let res: Response;
      try {
        res = await fetch(`${API_BASE}/api/dashboard/jamie/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          callbacks.onError(
            err instanceof Error ? err.message : "Failed to reach Jamie"
          );
        }
        return;
      }

      try {
        await handleMaintenanceResponse(res);
      } catch (err) {
        if (err instanceof MaintenanceError) {
          callbacks.onError(err.message);
          return;
        }
        throw err;
      }

      if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
          const body = (await res.json()) as { detail?: string; error?: string };
          message = body.detail ?? body.error ?? message;
        } catch {
          // ignore JSON parse errors on error body
        }
        callbacks.onError(message);
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) {
        callbacks.onError("Response body is not readable");
        return;
      }

      const decoder = new TextDecoder();
      let buffer = "";

      // Parse and dispatch all complete SSE events currently in `buffer`.
      const flushBuffer = () => {
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? ""; // last entry may be incomplete

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          // `error` field has two shapes — see Marie's flushBuffer for the
          // full rationale. Object form ⇒ structured upstream-error frame
          // (Sam, 2026-05-14). String form ⇒ legacy auth/config error.
          let data: {
            token?: string;
            done?: boolean;
            action?: JamieChatResponse["action"];
            clean_text?: string;
            type?: string;
            error?: string | UpstreamError;
          };
          try {
            data = JSON.parse(line.slice(6)) as typeof data;
          } catch {
            continue; // skip malformed lines
          }

          if (data.type === "error" && typeof data.error === "object" && data.error !== null) {
            const upstream = data.error as UpstreamError;
            callbacks.onUpstreamError?.(upstream);
            callbacks.onError(upstream.message);
          } else if (typeof data.error === "string") {
            callbacks.onError(data.error);
          } else if (data.token !== undefined) {
            callbacks.onToken(data.token);
          } else if (data.done) {
            callbacks.onDone(data.action ?? null, data.clean_text ?? "");
          }
        }
      };

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (value) {
            buffer += decoder.decode(value, { stream: !done });
          }

          flushBuffer();

          if (done) break;
        }

        // Flush any remaining buffer content once the stream is fully closed.
        // This handles the case where the final chunk (the `done` event) arrives
        // in the same read() call that signals stream completion.
        if (buffer.trim()) {
          buffer += "\n\n"; // ensure trailing event is treated as complete
          flushBuffer();
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          callbacks.onError(
            err instanceof Error ? err.message : "Stream read error"
          );
        }
      } finally {
        reader.releaseLock();
      }
    })();

    return controller;
  },
};

// ─── Marie Chat ───────────────────────────────────────────────

export interface MarieChatMessage {
  role: "user" | "assistant";
  content: string;
}

/**
 * First frame of a Marie chat stream when the backend has decided which
 * model/vision behavior to use for this turn (Sam, 2026-05-06).
 *
 * - `model_used`            — the model that actually answered. May differ
 *                             from the requested model when an image was
 *                             attached and the request had to be auto-
 *                             promoted to a multimodal sibling.
 * - `image_attached`        — whether pixels were sent to the model. False
 *                             means the backend gracefully degraded to
 *                             text-only (image file missing on disk, no
 *                             multimodal sibling available, etc.).
 */
export interface MarieChatMetaFrame {
  model_used: string;
  image_attached: boolean;
  /**
   * Backend-assigned MarieSession id for the turn. Populated as of 2026-05-06.
   * Older streams may omit this; consumers must treat it as optional.
   * After the first chat in a session, the frontend mirrors this id back
   * on every subsequent /api/marie/chat, /api/execute, and save-script call
   * so all activity is stitched into the same session record.
   */
  marie_session_id?: number | null;
  /**
   * Total images sent to the model this turn — sum of library/user-pick
   * (input_image_id) plus successfully-attached multipart files. Added
   * with the 2026-05-07 multipart cutover; older streams omit it.
   */
  image_count?: number;
  /**
   * Per-attachment skip reasons surfaced when the backend gracefully
   * degraded an individual image (oversize, bad MIME, decode failure,
   * count cap exceeded, etc.). Empty array on the happy path. Added
   * with the 2026-05-07 multipart cutover; older streams omit it.
   */
  warnings?: string[];
}

export interface MarieChatStreamCallbacks {
  onToken: (token: string) => void;
  onCodeUpdate: (code: string) => void;
  onDone: (cleanText: string) => void;
  onError: (message: string) => void;
  /**
   * Optional. Called once if the backend emits a leading meta frame
   * describing model selection + vision attachment status. Older
   * backends won't emit this; callers must not assume it will fire.
   */
  onMeta?: (meta: MarieChatMetaFrame) => void;
  /**
   * Optional. Fired when the backend rejects the request with a
   * structured 403 (`model_not_allowed`) or 429 (`quota_exceeded`)
   * tier-limit body. `onError` is ALSO invoked with the friendly
   * `detail.message` so callers that only subscribed to onError still
   * see the copy. Subscribe to this callback when you want to drive
   * UX state — e.g. swap the picker to an allowed model, disable the
   * send button for the remainder of the session.
   */
  onTierLimit?: (err: TierLimitError) => void;
  /**
   * Optional. Fired when the SSE stream delivers a structured upstream
   * error frame (Sam, 2026-05-14):
   *     data: { "type": "error", "error": <UpstreamError> }
   *
   * Surfaces transient AI-provider failures (overload, rate-limit,
   * timeout, etc.) with enough context to drive the friendly error
   * bubble. `onError` is ALSO invoked with `error.message` so callers
   * that only listen on onError still get the plain-text fallback —
   * subscribe to `onUpstreamError` only when you want to render the
   * richer category-keyed UI. The two paths are intentionally
   * non-exclusive: legacy callers keep working, modern callers
   * upgrade in place.
   *
   * The legacy bare-string frame `data: { "error": "..." }` is
   * unchanged and still routes to onError (used for auth/config errors).
   */
  onUpstreamError?: (err: UpstreamError) => void;
}

// Re-export so view-layer code can import the structured-error
// type from the same client module it already pulls other chat
// types from (keeps imports tidy in AiPanel + JamieChatView).
export type { UpstreamError, UpstreamErrorCategory } from "../types";

/**
 * Shape of the Marie chat request body. Both `input_image_id` and
 * `input_image_source` must be provided together — the backend rejects
 * lopsided payloads with HTTP 422. Pass neither for a text-only turn.
 */
export interface MarieChatRequest {
  message: string;
  history: MarieChatMessage[];
  model: string;
  current_code?: string;
  user_name?: string;
  /** FEI acquisition metadata source — preserved from earlier work. */
  image_id?: number | null;
  /** Vision attachment id (Phase 1 of multimodal Marie). */
  input_image_id?: number | null;
  /** Vision attachment source — must be set together with input_image_id. */
  input_image_source?: "user" | "library" | null;
  /**
   * Marie session id (capture audit). Pass to continue an existing session;
   * pass null + new_session=true to start a fresh session. Backend echoes
   * the resolved id back in the leading meta frame.
   */
  marie_session_id?: number | null;
  /**
   * When true, backend opens a brand-new MarieSession (and ignores any
   * marie_session_id sent alongside). Frontend sets this on "Start new chat"
   * and the first turn after a script load / sign-in.
   */
  new_session?: boolean;
  /**
   * Optional saved-script id this turn is associated with. Populated when
   * the user has a record from My Scripts loaded into the editor.
   */
  script_id?: number | null;
  /**
   * When true, this is a PROACTIVE auto-image-analysis turn (Mia, 2026-06-05):
   * the user just selected a library image or uploaded a temp image for the
   * editor's input, and the frontend asks Marie to post a short unprompted
   * description. The backend stores a clean "[auto] image analysis" sentinel
   * for the user turn (the `message` text is NOT persisted) and withholds the
   * code tool, so no `code_update` event is ever emitted for these turns. Send
   * the image the same way as a normal vision turn (input_image_id +
   * input_image_source for library/user picks, image_attachments[] for temp
   * uploads) and pass the current marie_session_id to keep the conversation
   * stitched together.
   */
  auto_image_analysis?: boolean;
}

/**
 * Request body for POST /api/marie/introduce-script. Sam (2026-05-08).
 * The backend returns a 1–2 sentence Marie-voiced introduction crediting
 * the script's author, used when a user loads a script from the community.
 * Endpoint is non-throwing in the steady state — on LLM failure the backend
 * falls back to a static templated reply (source: "fallback") so the
 * credit always lands. Network failures still throw client-side and the
 * caller must surface its own static fallback.
 */
export interface MarieIntroduceScriptRequest {
  title: string;
  author_name: string;
  description: string | null;
  code: string;
  /** Optional override for the model used to compose the intro. Backend defaults to gpt-5.4. */
  model?: string | null;
}

export interface MarieIntroduceScriptResponse {
  message: string;
  source: "llm" | "fallback";
}

export const marieApi = {
  /**
   * Generates a brief Marie-voiced intro for a community script load.
   * Always resolves to a usable message in the steady state — the backend
   * falls back to a static template on LLM failure. Callers should still
   * wrap this in try/catch for HTTP-level failures (network down, 4xx, 5xx)
   * and surface their own client-side fallback.
   *
   * Sam (2026-05-08).
   */
  introduceScript: (
    body: MarieIntroduceScriptRequest,
  ): Promise<MarieIntroduceScriptResponse> =>
    post<MarieIntroduceScriptResponse>(
      "/api/marie/introduce-script",
      body,
      authHeader(),
    ),

  /**
   * Streams the Marie (AI coding assistant) chat response via SSE.
   * Calls onToken for each arriving token, onDone when the stream completes
   * (with the final clean_text), and onError if anything goes wrong.
   * Returns an AbortController so the caller can cancel mid-stream.
   *
   * Wire format (Sam, 2026-05-07): `multipart/form-data` with two fields:
   *   - `payload`            JSON-stringified MarieChatRequest (always present)
   *   - `image_attachments`  Repeated file field, up to 4 files, ≤50 MB each.
   *                          Allowed MIMEs: image/png, image/jpeg, image/jpg,
   *                          image/tiff, image/x-tiff. Per-file failures don't
   *                          4xx the request — the backend surfaces skip
   *                          reasons in the leading meta frame's `warnings`
   *                          array and proceeds with whatever images survived.
   *
   * IMPORTANT: never set Content-Type manually — the browser fills in the
   * multipart boundary automatically when `body` is a FormData. Setting it
   * by hand strips the boundary and the backend rejects the request.
   *
   * The library/user-pick path stays as-is: it travels in the JSON `payload`
   * via `input_image_id` + `input_image_source`. Only disk-backed images
   * (today's "Upload temp image" + tomorrow's paperclip drop) ride the
   * `image_attachments` channel.
   */
  chatStream: (
    payload: MarieChatRequest,
    callbacks: MarieChatStreamCallbacks,
    imageFiles?: File[]
  ): AbortController => {
    const controller = new AbortController();

    (async () => {
      let res: Response;
      try {
        const token = localStorage.getItem("auth_token");
        const formData = new FormData();
        formData.append("payload", JSON.stringify(payload));
        if (imageFiles && imageFiles.length > 0) {
          for (const file of imageFiles) {
            formData.append("image_attachments", file, file.name);
          }
        }
        res = await fetch(`${API_BASE}/api/marie/chat`, {
          method: "POST",
          headers: {
            // No Content-Type here — the browser sets the multipart boundary.
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: formData,
          signal: controller.signal,
        });
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          callbacks.onError(
            err instanceof Error ? err.message : "Failed to reach Marie"
          );
        }
        return;
      }

      try {
        await handleMaintenanceResponse(res);
      } catch (err) {
        if (err instanceof MaintenanceError) {
          callbacks.onError(err.message);
          return;
        }
        throw err;
      }

      if (!res.ok) {
        let message = `HTTP ${res.status}`;
        let tierLimit: TierLimitError | null = null;
        try {
          const body = await res.json();
          tierLimit = tierLimitErrorFromBody(body);
          if (!tierLimit) {
            const b = body as { detail?: string; error?: string };
            message = b.detail ?? b.error ?? message;
          }
        } catch {
          // ignore JSON parse errors on error body
        }
        if (tierLimit) {
          // Structured 403/429 (Sam, 2026-05-14). Surface the typed
          // error first so the caller can drive UX state (swap picker
          // / disable input), then fall through to onError with the
          // friendly message for callers that only listen on onError.
          if (callbacks.onTierLimit) callbacks.onTierLimit(tierLimit);
          callbacks.onError(tierLimit.message);
          return;
        }
        callbacks.onError(message);
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) {
        callbacks.onError("Response body is not readable");
        return;
      }

      const decoder = new TextDecoder();
      let buffer = "";

      // Parse and dispatch all complete SSE events currently in `buffer`.
      // Frames in arrival order:
      //   1. (optional, new 2026-05-06) data: { "meta": {...} }
      //   2. data: { "token": "..." } — repeats
      //   2'. data: { "type": "code_update", "code": "..." } — interleaved
      //   3. data: { "done": true, "clean_text": "..." }
      // The meta frame is dispatched via onMeta when present; older backends
      // simply omit it and the stream proceeds straight into tokens.
      const flushBuffer = () => {
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? ""; // last entry may be incomplete

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          // `data.error` may be either:
          //   (a) string — legacy bare-string error frame (auth/config errors,
          //       2026-05-11 friendly-retry text). Route to onError.
          //   (b) UpstreamError object — only when `data.type === "error"`
          //       (Sam, 2026-05-14 structured upstream-error frame). Route
          //       to onUpstreamError AND onError(error.message) so listeners
          //       that haven't upgraded still get a plain-text message.
          let data: {
            token?: string;
            done?: boolean;
            clean_text?: string;
            error?: string | UpstreamError;
            type?: string;
            code?: string;
            meta?: MarieChatMetaFrame;
          };
          try {
            data = JSON.parse(line.slice(6)) as typeof data;
          } catch {
            continue; // skip malformed lines
          }

          if (data.meta !== undefined) {
            callbacks.onMeta?.(data.meta);
          } else if (data.type === "code_update" && data.code !== undefined) {
            callbacks.onCodeUpdate(data.code);
          } else if (data.type === "error" && typeof data.error === "object" && data.error !== null) {
            // Structured upstream-error frame (transient AI-provider failure).
            const upstream = data.error as UpstreamError;
            callbacks.onUpstreamError?.(upstream);
            // Always fire onError too — the legacy contract listeners
            // depend on it, and even modern callers use it to clear
            // streaming state. The structured handler decides whether
            // to surface the richer bubble.
            callbacks.onError(upstream.message);
          } else if (typeof data.error === "string") {
            // Legacy bare-string error frame (auth/config errors).
            callbacks.onError(data.error);
          } else if (data.token !== undefined) {
            callbacks.onToken(data.token);
          } else if (data.done) {
            callbacks.onDone(data.clean_text ?? "");
          }
        }
      };

      let receivedDone = false;
      const origOnDone = callbacks.onDone;
      const origOnError = callbacks.onError;
      const wrappedOnDone = (text: string) => { receivedDone = true; origOnDone(text); };
      const wrappedOnError = (msg: string) => { receivedDone = true; origOnError(msg); };
      (callbacks as any).onDone = wrappedOnDone;
      (callbacks as any).onError = wrappedOnError;

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (value) {
            buffer += decoder.decode(value, { stream: !done });
          }

          flushBuffer();

          if (done) break;
        }

        // Flush any remaining buffer content once the stream is fully closed.
        if (buffer.trim()) {
          buffer += "\n\n"; // ensure trailing event is treated as complete
          flushBuffer();
        }

        // If the stream closed without a done/error event (backend crash mid-stream),
        // call onDone so the UI always recovers from the streaming state.
        if (!receivedDone) {
          origOnDone("");
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          if (!receivedDone) {
            origOnError(err instanceof Error ? err.message : "Stream read error");
          }
        }
      } finally {
        reader.releaseLock();
      }
    })();

    return controller;
  },
};

// ─── Marie Sessions (capture / export) ────────────────────────
//
// Backend (Sam, 2026-05-06):
//   GET  /api/marie-sessions/current        → most recent session for caller
//   GET  /api/marie-sessions/{id}/export    → application/zip
//   GET  /api/admin/marie-sessions          → admin list (paginated, filtered)
//   POST /api/admin/marie-sessions/export   → bulk zip (max 50 ids)

/**
 * Header-only shape returned by the admin list endpoint. The full nested
 * turn/run/code payload is intentionally not exposed here — admins fetch
 * the zip when they need the transcript.
 */
export interface MarieSessionHeader {
  id: number;
  user_id: number | null;
  user_full_name: string | null;
  user_email: string | null;
  title: string | null;
  model: string | null;
  started_at: string;
  ended_at: string | null;
  turn_count: number;
  run_count: number;
  error_count: number;
  duration_ms: number | null;
}

// Nested-shape interfaces below mirror the backend serializers exactly:
//   • turns          → MarieTurn.to_dict()      (backend/models.py)
//   • code_snapshots → CodeSnapshot.to_dict()   (backend/models.py)
//   • runs           → _get_session_runs()      (backend/app.py ~7686)
// Verified against backend 2026-05-15. Field names here must stay in sync
// with those serializers — the admin in-app session viewer renders them.

export interface MarieSessionTurn {
  id: number;
  session_id: number;
  role: "user" | "assistant";
  content: string;
  sequence: number;
  image_attached: boolean;
  image_id_attached: number | null;
  model_used: string | null;
  promoted_from: string | null;
  created_at: string | null;
}

export interface MarieSessionRun {
  id: number;
  job_id: string;
  // ExecutionSession.status — "success" | "failure" | "timeout" in practice,
  // typed loosely so an unexpected backend value still renders.
  status: string;
  exit_code: number | null;
  stdout: string;
  stderr: string;
  duration_ms: number | null;
  created_at: string | null;
  completed_at: string | null;
}

export interface MarieSessionCodeSnapshot {
  id: number;
  session_id: number;
  code: string;
  source: string;
  label: string | null;
  captured_at: string | null;
}

/**
 * Full nested session — the shape produced by
 * `MarieSession.to_dict(include_nested=True, runs=runs)`. Returned by both
 * `GET /api/marie-sessions/current` and the admin
 * `GET /api/admin/marie-sessions/{id}` viewer endpoint.
 *
 * Note this is intentionally NOT `extends MarieSessionHeader`: the nested
 * `to_dict` payload carries `script_id` / `input_image_*` and omits the
 * list-only `user_full_name` / `user_email` / `duration_ms` fields the
 * admin list endpoint computes. `duration_ms` is derived client-side.
 */
export interface MarieSessionDetail {
  id: number;
  user_id: number | null;
  started_at: string;
  ended_at: string | null;
  model: string | null;
  script_id: number | null;
  input_image_id: number | null;
  input_image_source: string | null;
  title: string | null;
  turn_count: number;
  run_count: number;
  error_count: number;
  turns: MarieSessionTurn[];
  runs: MarieSessionRun[];
  code_snapshots: MarieSessionCodeSnapshot[];
}

export interface MarieSessionListParams {
  user_id?: number | null;
  date_from?: string | null;
  date_to?: string | null;
  has_errors?: boolean | null;
  model?: string | null;
  limit?: number;
  offset?: number;
}

export interface MarieSessionListResponse {
  total: number;
  limit: number;
  offset: number;
  sessions: MarieSessionHeader[];
}

/**
 * Trigger a browser download for a Blob (zip, etc). Centralised so the
 * "Download Session" button and the admin bulk-export both use the
 * same anchor-click trick — and so URL.revokeObjectURL is always called.
 */
function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari/Firefox finish the download before the URL dies.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const marieSessionsApi = {
  /**
   * Fetch the caller's most recent session (full nested data). Used as a
   * fallback when "Download Session" is clicked but the in-memory
   * marieSessionId is null (no chat yet this page-load).
   */
  current: (): Promise<MarieSessionDetail> =>
    get("/api/marie-sessions/current", authHeader()),

  /**
   * Download a single session as a zip blob. Caller is responsible for
   * triggering the browser-side download (use `triggerSessionDownload`
   * below for the standard filename).
   */
  exportZip: async (sessionId: number): Promise<Blob> => {
    const token = localStorage.getItem("auth_token");
    const res = await fetch(`${API_BASE}/api/marie-sessions/${sessionId}/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    await handleMaintenanceResponse(res);
    if (!res.ok) {
      let message = `HTTP ${res.status}`;
      try {
        const body = (await res.json()) as { detail?: string };
        message = body.detail ?? message;
      } catch {/* ignore */}
      throw new Error(message);
    }
    return res.blob();
  },

  /** Convenience wrapper: fetch + trigger browser download in one call. */
  triggerSessionDownload: async (sessionId: number): Promise<void> => {
    const blob = await marieSessionsApi.exportZip(sessionId);
    triggerBlobDownload(blob, `marie-session-${sessionId}.zip`);
  },
};

export const adminMarieSessionsApi = {
  /** Paginated, filterable list of sessions across all users. Admin-gated. */
  list: (params: MarieSessionListParams = {}): Promise<MarieSessionListResponse> => {
    const qs = new URLSearchParams();
    if (params.user_id != null) qs.set("user_id", String(params.user_id));
    if (params.date_from) qs.set("date_from", params.date_from);
    if (params.date_to) qs.set("date_to", params.date_to);
    if (params.has_errors) qs.set("has_errors", "true");
    if (params.model) qs.set("model", params.model);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const path = `/api/admin/marie-sessions${qs.toString() ? `?${qs.toString()}` : ""}`;
    return get(path, authHeader());
  },

  /**
   * Fetch the full nested detail of a single session (turns, code
   * snapshots, runs with stdout/stderr) for the in-app viewer. Admin-gated.
   * Response shape is identical to GET /api/marie-sessions/current.
   * Throws on 403 (not admin) / 404 (session not found) with the
   * backend's detail message.
   */
  detail: (sessionId: number): Promise<MarieSessionDetail> =>
    get(`/api/admin/marie-sessions/${sessionId}`, authHeader()),

  /**
   * Bulk export. Backend caps at 50 ids per request — UI must enforce
   * the same limit before calling. Returns the raw zip blob.
   */
  exportBulk: async (sessionIds: number[]): Promise<Blob> => {
    const token = localStorage.getItem("auth_token");
    const res = await fetch(`${API_BASE}/api/admin/marie-sessions/export`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ session_ids: sessionIds }),
    });
    await handleMaintenanceResponse(res);
    if (!res.ok) {
      let message = `HTTP ${res.status}`;
      try {
        const body = (await res.json()) as { detail?: string };
        message = body.detail ?? message;
      } catch {/* ignore */}
      throw new Error(message);
    }
    return res.blob();
  },

  /** Convenience wrapper: bulk export + browser-side download. */
  triggerBulkDownload: async (sessionIds: number[]): Promise<void> => {
    const blob = await adminMarieSessionsApi.exportBulk(sessionIds);
    const stamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, 19); // YYYY-MM-DDTHH-MM-SS
    triggerBlobDownload(blob, `marie-sessions-bulk-${stamp}.zip`);
  },

  /**
   * Permanently delete a selection of Marie sessions and all their nested
   * data (turns, code snapshots, run output artifacts). Backend caps the
   * batch at 50 ids — UI must enforce the same limit. Admin JWT only;
   * the X-Admin-Password requirement was dropped 2026-05-06 in favor of
   * a plain "are you sure" confirmation dialog.
   */
  deleteBulk: (
    sessionIds: number[],
  ): Promise<{ deleted: number; not_found: number }> =>
    request<{ deleted: number; not_found: number }>(
      "/api/admin/marie-sessions",
      {
        method: "DELETE",
        body: JSON.stringify({ session_ids: sessionIds }),
      },
      authHeader(),
    ),

  /**
   * Permanently delete every Marie session across all users. Admin JWT
   * only — same simplified gate as deleteBulk.
   */
  deleteAll: (): Promise<{ deleted: number }> =>
    request<{ deleted: number }>(
      "/api/admin/marie-sessions/all",
      { method: "DELETE" },
      authHeader(),
    ),
};

// ─── Meta ─────────────────────────────────────────────────────

export const metaApi = {
  get: (): Promise<DashboardMeta> => get("/api/dashboard/meta"),
};

// ─── Tags ─────────────────────────────────────────────────────

export const tagsApi = {
  suggested: (): Promise<{ tags: string[] }> => get("/api/dashboard/tags"),
};

// ─── User Scripts ─────────────────────────────────────────────

export interface UserScript {
  id: number;
  title: string;
  description: string | null;
  code: string;
  is_public: boolean;
  avg_rating: number;
  rating_count: number;
  created_at: string;
  updated_at: string;
  /**
   * Persistent versioning (Sam, 2026-05-11). The id of the version row
   * currently considered "current" — the one whose `code` is mirrored on
   * `user_scripts.code` for fast loads. Null only on legacy scripts that
   * pre-date the migration; new scripts always have a head.
   */
  head_version_id: number | null;
  /**
   * The version row pinned for community publishing. Null means the script
   * is not currently published (or the user has never picked a pinned
   * version). Decoupled from `head_version_id` so editors can keep iterating
   * without disturbing what the community sees.
   */
  published_version_id: number | null;
  /**
   * Paired image id (Sam, 2026-05-13). Set when the author publishes with an
   * image attached so other users get the matching sample when they load the
   * script from the Community tab. Null when the script was published without
   * an image or has never been published. Also present on starter scripts
   * (same column on the backend, exposed on community endpoints as of
   * 2026-05-13).
   */
  default_image_id: number | null;
  /**
   * Number of times this script has been forked (Sam, 2026-05-15).
   * Incremented server-side by `POST /api/scripts/{id}/fork` on the
   * original script. New scripts start at 0.
   */
  fork_count: number;
  /**
   * If this script was created by forking another, the id of the source
   * script (Sam, 2026-05-15). Null for scripts that were authored directly
   * rather than forked.
   */
  forked_from_script_id: number | null;
}

/**
 * One row from `script_versions` — an immutable snapshot of a script at a
 * point in time. Code is omitted from the list endpoint; fetch a specific
 * version (GET /api/scripts/:id/versions/:n) when the bytes are needed.
 */
export interface ScriptVersion {
  id: number;
  user_script_id: number;
  version_number: number;
  notes: string | null;
  parent_version_id: number | null;
  created_at: string;
  /**
   * Only present on the GET single-version endpoint and on the create
   * response. Optional in the type so the same interface can describe
   * list-shaped (no code) and detail-shaped (with code) rows.
   */
  code?: string;
}

export interface ScriptVersionsResponse {
  head_version_id: number | null;
  published_version_id: number | null;
  versions: ScriptVersion[];
}

export interface ScriptVersionCreatePayload {
  code: string;
  notes?: string | null;
}

/** Response from POST /api/scripts/:id/versions — both pieces returned together. */
export interface ScriptVersionCreateResponse {
  version: ScriptVersion;
  script: UserScript;
}

/** Community script — same as UserScript but includes the author's display name. */
export interface CommunityScript extends UserScript {
  author_name: string;
}

export interface UserScriptCreatePayload {
  title: string;
  description?: string | null;
  code: string;
  is_public: boolean;
  /** Optional Marie session id to stitch the saved script into the session capture. */
  marie_session_id?: number | null;
  /**
   * Paired sample image (Sam, 2026-05-14). Three-state field with the same
   * semantics + validation as PUT — distinguished server-side via
   * `model_fields_set`, so the JSON body must mirror that:
   *   • Omit the key entirely → new script created with `default_image_id = null`.
   *   • Explicit `null`       → new script created with `default_image_id = null`.
   *   • Integer id            → validates + sets the image. Image must exist
   *     and be accessible to the caller (owned or globally shared); 422 if
   *     not found, 403 if not accessible. Validation runs BEFORE the script
   *     row is inserted, so a failure produces no orphan row. No auto-share
   *     side-effect — that only happens at publish time.
   *
   * Because `JSON.stringify` drops `undefined` keys but preserves `null`,
   * callers should send `null` as a literal `null` and OMIT the key from
   * the payload object when they want the "no image" behavior (which is
   * equivalent on create — both produce a null pairing).
   */
  default_image_id?: number | null;
}

export interface UserScriptUpdatePayload {
  title?: string;
  description?: string | null;
  code?: string;
  is_public?: boolean;
  /** Optional Marie session id to stitch the saved script into the session capture. */
  marie_session_id?: number | null;
  /**
   * Restore-as-current. When set, the backend (Sam, 2026-05-11) flips
   * `user_scripts.head_version_id` to this row and re-mirrors that row's
   * code onto `user_scripts.code`. Validated server-side: must belong to
   * this script (400 otherwise). If `code` is also present in the same
   * body, the head flip happens first and the code overwrite second.
   */
  head_version_id?: number;
  /**
   * Paired sample image (Sam, 2026-05-14). Three-state field — distinguished
   * server-side via `model_fields_set`, so the JSON body must mirror that:
   *   • Omit the key entirely → existing `default_image_id` is left unchanged.
   *   • Explicit `null`       → clears the image association.
   *   • Integer id            → validates + sets the image. Image must exist
   *     and be accessible to the caller (owned or globally shared); 422 if
   *     not found, 403 if not accessible. No auto-share side-effect from
   *     this endpoint — that only happens at publish time.
   *
   * Because `JSON.stringify` drops `undefined` keys but preserves `null`,
   * callers should send `null` as a literal `null` and OMIT the key from
   * the payload object when they want the "no change" behavior.
   */
  default_image_id?: number | null;
}

/**
 * One rating row from `GET /api/scripts/{script_id}/ratings`. The list is
 * sorted server-side by `created_at` DESC. `is_own_rating` is true for the
 * row authored by the calling user (used to render edit affordances).
 */
export interface ScriptRatingEntry {
  id: number;
  rating: number;
  review_note: string | null;
  author_name: string;
  is_own_rating: boolean;
  created_at: string;
}

export const userScriptsApi = {
  list: (): Promise<{ scripts: UserScript[] }> =>
    get("/api/scripts", authHeader()),

  create: (payload: UserScriptCreatePayload): Promise<UserScript> =>
    post("/api/scripts", payload, authHeader()),

  update: (id: number, payload: UserScriptUpdatePayload): Promise<UserScript> =>
    put(`/api/scripts/${id}`, payload, authHeader()),

  delete: (id: number): Promise<void> =>
    del(`/api/scripts/${id}`, authHeader()),

  /**
   * Toggle community visibility. Returns the updated script.
   *
   * When publishing (isPublic = true), an optional `imageId` pairs a sample
   * image with the script so other users get the matching image when they
   * load it from the Community tab. If the supplied image is a private
   * (user-owned) image, the backend auto-shares it (flips `is_global=true`)
   * — by design (Greg, 2026-05-13). Pass null/undefined for `imageId` to
   * publish without an image. The unpublish path (isPublic = false) ignores
   * `imageId` entirely.
   */
  publish: (
    id: number,
    isPublic: boolean,
    imageId?: number | null,
  ): Promise<UserScript> =>
    post(
      `/api/scripts/${id}/publish`,
      { is_public: isPublic, image_id: imageId ?? null },
      authHeader(),
    ),

  /**
   * Submit a 1–5 star rating with an optional written review note.
   * `reviewNote` may be null/undefined; whitespace-only strings should be
   * trimmed to null by the caller. Server enforces max 280 chars (422).
   * Returns the updated aggregate.
   */
  rate: (
    id: number,
    rating: number,
    reviewNote?: string | null,
  ): Promise<{ avg_rating: number; rating_count: number }> =>
    post(
      `/api/scripts/${id}/rate`,
      { rating, review_note: reviewNote ?? null },
      authHeader(),
    ),

  /**
   * POST /api/scripts/:id/fork — server-side fork (Sam, 2026-05-15).
   * Copies the target script into the caller's collection as
   * "<title> (forked)", seeds a clean v1, sets `forked_from_script_id`
   * on the copy, and increments `fork_count` on the original. Returns
   * the newly created copy.
   * Errors: 400 (cannot fork own script), 404 (not found / private),
   * 401 (unauthenticated).
   */
  fork: (scriptId: number): Promise<UserScript> =>
    post(`/api/scripts/${scriptId}/fork`, {}, authHeader()),

  // ── Versioning (Sam, 2026-05-11) ───────────────────────────
  // Listing returns versions sorted DESC by version_number; code is
  // omitted on list rows. Fetch a single version to get its bytes.

  /** GET /api/scripts/:id/versions — all versions for this script (no code). */
  listVersions: (scriptId: number): Promise<ScriptVersionsResponse> =>
    get(`/api/scripts/${scriptId}/versions`, authHeader()),

  /** GET /api/scripts/:id/versions/:n — a single version, with code. */
  getVersion: (scriptId: number, versionNumber: number): Promise<ScriptVersion> =>
    get(`/api/scripts/${scriptId}/versions/${versionNumber}`, authHeader()),

  /**
   * POST /api/scripts/:id/versions — create a new version row (auto-numbered).
   * Sets the new row as head and refreshes user_scripts.code.
   * Use this for the "Create new vN" save path.
   */
  createVersion: (
    scriptId: number,
    payload: ScriptVersionCreatePayload,
  ): Promise<ScriptVersionCreateResponse> =>
    post(`/api/scripts/${scriptId}/versions`, payload, authHeader()),

  /**
   * PUT /api/scripts/:id/versions/:n/notes — edit the freeform notes on an
   * existing version. Code on a version row is immutable; notes are not.
   * Pass `null` to clear notes.
   */
  updateVersionNotes: (
    scriptId: number,
    versionNumber: number,
    notes: string | null,
  ): Promise<ScriptVersion> =>
    put(
      `/api/scripts/${scriptId}/versions/${versionNumber}/notes`,
      { notes },
      authHeader(),
    ),
};

// ─── Community Scripts ────────────────────────────────────────

export const communityApi = {
  /** Returns all public scripts, sorted by avg_rating desc (server-side). */
  list: (): Promise<CommunityScript[]> =>
    get("/api/scripts/community", authHeader()),

  /**
   * Returns the per-script ratings list (DESC by created_at). Each entry
   * includes the author's display name and whether it is the calling user's
   * own rating. Auth required.
   */
  listRatings: (scriptId: number): Promise<ScriptRatingEntry[]> =>
    get(`/api/scripts/${scriptId}/ratings`, authHeader()),
};

// ─── Auth ─────────────────────────────────────────────────────

/**
 * Tier role on a user. Sam (2026-05-14) — fair-share QoS tiering.
 *   - "basic"    → rate-limited (Marie daily cap, script-run hourly cap),
 *                  restricted to `basic_allowed_models` (server-side).
 *   - "advanced" → bypasses every basic limit; full model access.
 *   - "admin"    → bypasses + admin panel access. `is_admin` stays in sync
 *                  with this value via the backend.
 */
export type UserRole = "basic" | "advanced" | "admin";

export interface AuthUser {
  id: number;
  email: string;
  full_name: string | null;
  is_admin: boolean;
  /**
   * Tier role. Defaults to "basic" for newly registered accounts. Older
   * cached `auth_user` blobs (pre-2026-05-14) won't have this field; UI
   * code that reads `role` should default to "basic" until /api/auth/me
   * returns the fresh shape.
   */
  role?: UserRole;
  provider?: string;
  has_seen_welcome_video: boolean;
  has_seen_quickstart_video: boolean;
  has_run_first_script: boolean;
  /**
   * Whether the user has dismissed the one-time "we log some data about your
   * Marie chats" informational notice. Present on every auth response (login,
   * register, SSO callback, /api/auth/me) because they all build through one
   * server-side helper. Older cached `auth_user` blobs (pre-2026-06-05) won't
   * have this field; treat absence as `true` so we don't re-show the notice to
   * existing sessions before the next /api/auth/me round-trip overwrites it.
   */
  logging_notice_acknowledged: boolean;
  /**
   * Marie model ids THIS user is allowed to send. Server-filtered:
   * for basic → intersection of `basic_allowed_models` and the catalog;
   * for advanced/admin → every catalog id. Marie's model picker filters
   * on this list. Older cached `auth_user` blobs (pre-2026-05-14) won't
   * have this field; treat absence as "every catalog id" so we don't
   * lock anyone out before the next /api/auth/me round-trip.
   */
  allowed_models?: string[];
}

export interface AuthTokenResponse {
  access_token: string;
  token_type: string;
  user: AuthUser;
}

function authHeader(): Record<string, string> {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const authApi = {
  register: (
    email: string,
    password: string,
    full_name?: string
  ): Promise<AuthTokenResponse> =>
    post("/api/auth/register", { email, password, ...(full_name ? { full_name } : {}) }),

  login: (email: string, password: string): Promise<AuthTokenResponse> =>
    post("/api/auth/login", { email, password }),

  me: (): Promise<AuthUser> =>
    get("/api/auth/me", authHeader()),

  logout: (): Promise<{ message: string }> =>
    post("/api/auth/logout", undefined, authHeader()),

  /**
   * Marks the welcome intro video as seen for the current user. Returns the
   * updated AuthUser (with `has_seen_welcome_video: true`). Backend is
   * idempotent — safe to call multiple times.
   */
  markWelcomeVideoSeen: (): Promise<AuthUser> =>
    post("/api/users/me/welcome-video-seen", undefined, authHeader()),

  /**
   * Marks the quickstart video as seen for the current user. Returns the
   * updated AuthUser (with `has_seen_quickstart_video: true`). Backend is
   * idempotent — safe to call multiple times. Mirror of markWelcomeVideoSeen.
   */
  markQuickstartVideoSeen: (): Promise<AuthUser> =>
    post("/api/users/me/quickstart-video-seen", undefined, authHeader()),

  /**
   * Acknowledges the one-time data-logging informational notice for the
   * current user. Returns the updated AuthUser (with
   * `logging_notice_acknowledged: true`). No request body. Backend is
   * idempotent — safe to call multiple times.
   */
  acknowledgeLoggingNotice: (): Promise<AuthUser> =>
    post("/api/user/acknowledge-logging-notice", undefined, authHeader()),
};

// ─── Images ───────────────────────────────────────────────────

export interface AcquisitionMetadata {
  pixel_size_nm?: number;
  hfw_um?: number;
  vfw_um?: number;
  kV?: number;
  beam_current_pA?: number;
  dwell_us?: number;
  working_distance_mm?: number;
  detector?: string;
  signal?: string;
  instrument?: string;
  date_acquired?: string;
}

export type MetadataSource = "extracted" | "manual" | "partial" | "none";

/**
 * Auto-derived, search-friendly tags computed from FEI acquisition metadata
 * at upload time (Sam, 2026-05-05). All five keys are always present on the
 * server payload. `calibrated` is always boolean; the others are nullable
 * when the value couldn't be derived. See PROGRESS.md (2026-05-05) for the
 * full mapping rules.
 */
export interface DerivedTags {
  modality: "SEM" | "TEM" | "SDB" | "Optical" | string | null;
  magnification: "Low-mag" | "Mid-mag" | "High-mag" | string | null;
  signal: "BSE" | "SE" | "STEM-BF" | "STEM-HAADF" | string | null;
  calibrated: boolean;
  instrument: string | null;
}

export interface UserImage {
  id: number;
  name: string;
  description: string;
  image_type: "SEM" | "TEM" | "SDB" | "OPTICAL";
  format: string;
  tags: string | null;
  is_global: boolean;
  file_size: number;
  created_at: string;
  original_filename?: string;
  acquisition_metadata?: AcquisitionMetadata | null;
  metadata_source?: MetadataSource | null;
  derived_tags?: DerivedTags | null;
}

export interface LibraryImage {
  id: number;
  user_id?: number;
  name: string;
  description: string;
  image_type: "SEM" | "TEM" | "SDB" | "OPTICAL";
  format: string;
  tags: string | null;
  is_global: boolean;
  file_size: number;
  created_at: string;
  uploader_name?: string;
  acquisition_metadata?: AcquisitionMetadata | null;
  metadata_source?: MetadataSource | null;
  derived_tags?: DerivedTags | null;
}

/**
 * Filters for image-list endpoints. All optional. Repeatable string fields
 * pass each value as a separate query param entry (FastAPI parses repeated
 * params into a list). Different filter keys AND together; values within a
 * key OR together. `calibrated` is a single boolean (not repeatable).
 */
export interface ImageListFilters {
  modality?: string[];
  magnification?: string[];
  signal?: string[];
  instrument?: string[];
  calibrated?: boolean;
}

function buildImageFilterQuery(filters?: ImageListFilters): string {
  if (!filters) return "";
  const parts: string[] = [];
  const append = (key: string, values?: string[]) => {
    if (!values || values.length === 0) return;
    for (const v of values) parts.push(`${key}=${encodeURIComponent(v)}`);
  };
  append("modality", filters.modality);
  append("magnification", filters.magnification);
  append("signal", filters.signal);
  append("instrument", filters.instrument);
  if (filters.calibrated !== undefined) {
    parts.push(`calibrated=${filters.calibrated}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export interface ImageMetadataResponse {
  metadata_source: MetadataSource;
  parsed: AcquisitionMetadata | null;
  raw_fei_ini: string | null;
}

export const imageApi = {
  upload: (formData: FormData): Promise<UserImage> => {
    const token = localStorage.getItem("auth_token");
    const headers: Record<string, string> = token
      ? { Authorization: `Bearer ${token}` }
      : {};
    return request<UserImage>(
      "/api/images/upload",
      { method: "POST", body: formData },
      headers
    );
  },

  getMyImages: (filters?: ImageListFilters): Promise<UserImage[]> =>
    get(`/api/images/my${buildImageFilterQuery(filters)}`, authHeader()),

  /** Replaces the old getGlobalImages + getLibraryImages — calls the unified shared endpoint. */
  getSharedImages: (filters?: ImageListFilters): Promise<LibraryImage[]> =>
    get(`/api/images/shared${buildImageFilterQuery(filters)}`, authHeader()),

  /** @deprecated Use getSharedImages instead. Kept for any lingering call sites. */
  getGlobalImages: (): Promise<LibraryImage[]> =>
    get("/api/images/shared", authHeader()),

  /** @deprecated Use getSharedImages instead. Kept for any lingering call sites. */
  getLibraryImages: (): Promise<LibraryImage[]> =>
    get("/api/images/shared", authHeader()),

  /** Copy a shared image into the current user's personal library. Returns the new UserImage (201). */
  saveToLibrary: (imageId: number): Promise<UserImage> =>
    request<UserImage>(
      `/api/images/${imageId}/save`,
      { method: "POST" },
      authHeader()
    ),

  deleteImage: (id: number): Promise<void> =>
    del(`/api/images/${id}`, authHeader()),

  setVisibility: (id: number, isGlobal: boolean): Promise<UserImage> =>
    request<UserImage>(
      `/api/images/${id}/visibility`,
      { method: "PATCH", body: JSON.stringify({ is_global: isGlobal }) },
      { "Content-Type": "application/json", ...authHeader() }
    ),

  /** Returns parsed acquisition metadata + the raw FEI INI text (re-extracted from disk). */
  getMetadata: (id: number): Promise<ImageMetadataResponse> =>
    get(`/api/images/${id}/metadata`, authHeader()),

  /**
   * PUT user-supplied acquisition metadata. The body is the new desired state —
   * fields omitted are cleared. Source transitions to 'manual' (was none),
   * 'partial' (was extracted/partial), or stays 'manual'.
   * Returns the full updated UserImage.
   */
  updateMetadata: (id: number, fields: AcquisitionMetadata): Promise<UserImage> =>
    request<UserImage>(
      `/api/images/${id}/metadata`,
      { method: "PUT", body: JSON.stringify(fields) },
      { "Content-Type": "application/json", ...authHeader() },
    ),

  /**
   * PATCH the image's identity fields — name, description, tags. All optional;
   * send only the fields you want to change. Owner-scoped.
   */
  updateImage: (
    id: number,
    fields: { name?: string; description?: string; tags?: string | null },
  ): Promise<UserImage> =>
    request<UserImage>(
      `/api/images/${id}`,
      { method: "PATCH", body: JSON.stringify(fields) },
      { "Content-Type": "application/json", ...authHeader() },
    ),

  /**
   * Fetches the binary image file and returns a blob URL.
   * The caller is responsible for revoking the URL when done.
   */
  getImageUrl: async (id: number): Promise<string> => {
    const token = localStorage.getItem("auth_token");
    const headers: HeadersInit = token
      ? { Authorization: `Bearer ${token}` }
      : {};
    const res = await fetch(`${API_BASE}/api/images/file/${id}`, { headers });
    await handleMaintenanceResponse(res);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  /**
   * Fetches a 400px-wide thumbnail PNG and returns a blob URL.
   * Much faster than getImageUrl for large TIFFs.
   * The caller is responsible for revoking the URL when done.
   */
  getThumbnailUrl: async (id: number): Promise<string> => {
    const token = localStorage.getItem("auth_token");
    const headers: HeadersInit = token
      ? { Authorization: `Bearer ${token}` }
      : {};
    const res = await fetch(`${API_BASE}/api/images/thumb/${id}`, { headers });
    await handleMaintenanceResponse(res);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
};

// ─── System Library (Admin) ───────────────────────────────────

export interface LibraryScript {
  id: number;
  title: string;
  description: string | null;
  code: string;
  author: string | null;
  tags: string[];
  category: string | null;
  default_image_id: number | null;
  average_rating: number;
  rating_count: number;
  created_at: string;
  updated_at: string;
}

export interface LibraryScriptCategory {
  name: string;
  scripts: LibraryScript[];
}

export interface LibraryScriptListResponse {
  categories: LibraryScriptCategory[];
  uncategorized: LibraryScript[];
}

export const adminSystemImagesApi = {
  list: (): Promise<UserImage[]> =>
    get("/api/admin/system/images", authHeader()),

  upload: (formData: FormData): Promise<UserImage> => {
    const token = localStorage.getItem("auth_token");
    const headers: Record<string, string> = token
      ? { Authorization: `Bearer ${token}` }
      : {};
    return request<UserImage>(
      "/api/admin/system/images",
      { method: "POST", body: formData },
      headers
    );
  },

  update: (id: number, body: Partial<Pick<UserImage, "name" | "description" | "image_type" | "tags">>): Promise<UserImage> =>
    request<UserImage>(
      `/api/admin/system/images/${id}`,
      { method: "PATCH", body: JSON.stringify(body) },
      { "Content-Type": "application/json", ...authHeader() }
    ),

  remove: (id: number): Promise<void> =>
    del(`/api/admin/system/images/${id}`, authHeader()),
};

export const adminSystemScriptsApi = {
  list: (): Promise<LibraryScript[]> =>
    get("/api/admin/system/scripts", authHeader()),

  create: (body: {
    title: string;
    code: string;
    description?: string;
    category?: string;
    tags?: string[];
  }): Promise<LibraryScript> =>
    post("/api/admin/system/scripts", body, authHeader()),

  update: (id: number, body: Partial<{
    title: string;
    code: string;
    description: string;
    category: string;
    tags: string[];
  }>): Promise<LibraryScript> =>
    put(`/api/admin/system/scripts/${id}`, body, authHeader()),

  remove: (id: number): Promise<void> =>
    del(`/api/admin/system/scripts/${id}`, authHeader()),
};

export const libraryScriptsApi = {
  list: (): Promise<LibraryScriptListResponse> =>
    get("/api/scripts/library", authHeader()),
};

// ─── Admin Users ──────────────────────────────────────────────

export interface AdminUser {
  id: number;
  email: string;
  full_name: string | null;
  username: string | null;
  is_admin: boolean;
  /**
   * Tier role (Sam, 2026-05-14). Stays in sync with `is_admin`: setting
   * `role='admin'` flips `is_admin=true`, setting basic/advanced flips
   * it false. The legacy set-admin endpoint also writes this column.
   */
  role: UserRole;
  is_active: boolean;
  provider: string;
  created_at: string;
  last_login: string | null;
  /**
   * Job title (Sam, 2026-05-15). Captured from the TF SSO ID token at
   * login, with a Microsoft Graph `/me` fallback on first login. Used
   * server-side to auto-classify developer/IT users into the `basic`
   * role at user creation. Read-only on the frontend — there is no
   * inline-edit affordance; the value refreshes from the IdP on each
   * login. Null for users whose IdP didn't supply a title (e.g. local
   * accounts seeded outside SSO).
   */
  job_title: string | null;
  /**
   * Total scripts this user has saved (Sam, 2026-05-14 follow-up).
   * Counts every row in `user_scripts` regardless of `is_public`.
   */
  saved_scripts_count: number;
  /**
   * Subset of saved scripts that are published to the community
   * (Sam, 2026-05-14 follow-up). Backed by `user_scripts.is_public`.
   */
  published_scripts_count: number;
}

export const adminUsersApi = {
  list: (): Promise<AdminUser[]> =>
    get("/api/admin/users", authHeader()),

  setAdmin: (id: number, isAdmin: boolean): Promise<AdminUser> =>
    patch(`/api/admin/users/${id}/set-admin`, { is_admin: isAdmin }, authHeader()),

  /**
   * Sets the tier role for a user. Admin JWT only — Sam dropped the
   * X-Admin-Password gate in the 2026-05-14 follow-up (a logged-in admin
   * shouldn't be re-prompted just to flip a dropdown). Backend keeps
   * `is_admin` in sync with `role`. Guards still enforced server-side:
   * cannot change your own role, cannot modify the system account.
   */
  setRole: (
    id: number,
    role: UserRole,
  ): Promise<AdminUser> =>
    put(
      `/api/admin/users/${id}/role`,
      { role },
      authHeader(),
    ),

  remove: (id: number): Promise<void> =>
    del(`/api/admin/users/${id}`, authHeader()),
};

// ─── Execution ────────────────────────────────────────────────

export interface ExecutionOutputImage {
  name: string;
  url: string;
}

export type ExecutionOutputFileType = "image" | "pdf" | "csv" | "xlsx" | "text" | "other";

export interface OverlayShapeBase {
  type: "rectangle" | "ellipse" | "point" | "polyline";
  stroke?: string;
  stroke_width?: number;
  stroke_dash?: string;
  fill?: string;
  rotation?: number;
  label?: string;
  color?: string;
}

export interface OverlayRectangle extends OverlayShapeBase {
  type: "rectangle";
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface OverlayEllipse extends OverlayShapeBase {
  type: "ellipse";
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

export interface OverlayPoint extends OverlayShapeBase {
  type: "point";
  x: number;
  y: number;
}

export interface OverlayPolyline extends OverlayShapeBase {
  type: "polyline";
  points: Array<[number, number]>;
  closed?: boolean;
}

export type OverlayShape = OverlayRectangle | OverlayEllipse | OverlayPoint | OverlayPolyline;

export interface ImageOverlay {
  version: 1;
  shapes: OverlayShape[];
  source_file: string;
}

export interface ExecutionOutputFile {
  name: string;
  ext: string;
  type: ExecutionOutputFileType;
  size_bytes: number;
  url: string;
  overlay?: ImageOverlay;
}

export interface ExecutionResult {
  job_id: string;
  status: "success" | "failure" | "timeout";
  exit_code: number;
  /** Merged stdout + stderr, kept for back-compat. Prefer `stdout` + `stderr` separately. */
  console: string;
  /** Stdout only (post 2026-05-06 separation). May be empty on infrastructure-error fallback paths. */
  stdout?: string;
  /** Stderr only (post 2026-05-06 separation). Where Python tracebacks live. */
  stderr?: string;
  duration_ms: number;
  /** @deprecated — retained for backwards compat. Prefer `output_files.filter(f => f.type === "image")`. */
  output_images?: ExecutionOutputImage[];
  output_files?: ExecutionOutputFile[];
}

export const executeApi = {
  /**
   * Run a script. Sends multipart/form-data so an optional temp image file
   * can be included without ever saving it to the image library.
   *
   * `marieSessionId` (when supplied) attaches this run to an active Marie
   * session so the run row, console output, and any errors are stitched
   * into the session capture. Only sent when present — older sessions
   * (no chat yet) simply omit it and the backend treats the run as
   * standalone.
   */
  run: (
    script: string,
    imageId?: number,
    imageFile?: File,
    parameters?: string,
    marieSessionId?: number | null,
  ): Promise<ExecutionResult> => {
    const fd = new FormData();
    fd.append("script", script);
    if (imageId != null) fd.append("image_id", String(imageId));
    if (imageFile != null) fd.append("image", imageFile);
    if (parameters && parameters.trim()) fd.append("parameters", parameters);
    if (marieSessionId != null) fd.append("marie_session_id", String(marieSessionId));
    const token = localStorage.getItem("auth_token");
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    return request<ExecutionResult>("/api/execute", { method: "POST", body: fd }, headers);
  },

  /** Fetch a temporary execution output image as a blob URL. */
  getOutputImageUrl: async (url: string): Promise<string> => {
    const token = localStorage.getItem("auth_token");
    const res = await fetch(`${API_BASE}${url}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    await handleMaintenanceResponse(res);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return URL.createObjectURL(await res.blob());
  },

  /** Fetch any execution output file as a Blob (auth-aware). */
  getOutputFileBlob: async (url: string): Promise<Blob> => {
    const token = localStorage.getItem("auth_token");
    const res = await fetch(`${API_BASE}${url}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    await handleMaintenanceResponse(res);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.blob();
  },

  /** Fetch any execution output file as text (auth-aware). */
  getOutputFileText: async (url: string): Promise<string> => {
    const token = localStorage.getItem("auth_token");
    const res = await fetch(`${API_BASE}${url}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    await handleMaintenanceResponse(res);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.text();
  },
};

// ─── Maintenance Mode ─────────────────────────────────────────
//
// Public read endpoint (status), admin-gated write endpoint (set).
// Backend: GET /api/system/maintenance returns the current state;
// POST /api/admin/maintenance flips it. Sam's 2026-05-15 follow-up
// reworked `verify_admin` to accept EITHER an admin JWT OR the legacy
// X-Admin-Password header, so the frontend now sends only the JWT —
// no password prompt on toggle.

export interface MaintenanceStatus {
  enabled: boolean;
  message: string | null;
  updated_at: string | null;
}

export const maintenanceApi = {
  /** Public — no auth required. Always reachable, even during maintenance. */
  status: (): Promise<MaintenanceStatus> => get("/api/system/maintenance"),

  /** Admin only. JWT-gated (see verify_admin on the backend). */
  set: (
    enabled: boolean,
    message: string | null,
  ): Promise<MaintenanceStatus> =>
    post(
      "/api/admin/maintenance",
      { enabled, message },
      authHeader(),
    ),
};

// ─── Role quotas / tier limits (admin) ────────────────────────
//
// Sam (2026-05-14) — runtime-configurable QoS knobs for `basic` users:
// daily Marie chat cap, hourly script-run cap, allowed model subset.
// Three roles total — `basic` (throttled), `advanced` (bypass),
// `admin` (bypass + admin panel). Quotas have NO effect on advanced /
// admin tiers; they exist only to ensure scientists aren't crowded out
// of Opus's 600-RPM global cap by power users hammering Marie.
//
// • GET `/api/admin/role-quotas` — JWT admin only. Returns the persisted
//   row plus the full model catalog so the admin UI can render the
//   allowlist checkbox grid against the authoritative set of model ids.
// • PUT `/api/admin/role-quotas` — JWT admin only (Sam's 2026-05-15
//   follow-up: `verify_admin` accepts an admin JWT OR the legacy
//   X-Admin-Password header). Full replace; partial-update is not
//   supported. Invalidates the backend's in-process cache synchronously
//   so changes take effect on the next request rather than after the
//   5-second TTL ceiling.

export interface AvailableMarieModel {
  id: string;
  label: string;
  provider: string;
}

export interface RoleQuotaSettings {
  basic_marie_chats_per_day: number;
  basic_script_runs_per_hour: number;
  basic_allowed_models: string[];
  updated_at: string | null;
  updated_by: string | null;
  available_models: AvailableMarieModel[];
}

export interface RoleQuotaUpdatePayload {
  basic_marie_chats_per_day: number;
  basic_script_runs_per_hour: number;
  basic_allowed_models: string[];
}

export const roleQuotasApi = {
  /** Admin only (JWT). Returns the persisted row plus the model catalog. */
  get: (): Promise<RoleQuotaSettings> =>
    get("/api/admin/role-quotas", authHeader()),

  /**
   * Full-replace update. JWT admin only (Sam's 2026-05-15 follow-up:
   * `verify_admin` accepts an admin JWT OR the legacy X-Admin-Password
   * header — the frontend now sends only the JWT). Backend validates
   * positives + non-empty allowlist; on unknown model id, returns 400
   * with the offending ids in `detail`.
   */
  set: (
    payload: RoleQuotaUpdatePayload,
  ): Promise<RoleQuotaSettings> =>
    put(
      "/api/admin/role-quotas",
      payload,
      authHeader(),
    ),
};

// ─── Domain restriction (admin) ───────────────────────────────
//
// Sam (2026-05-20) — beta access control. A master toggle plus a managed
// list of approved email domains. While ON, register/login/SSO are
// blocked for anyone whose email domain isn't in the list (admins are
// always exempt). Greg flips the toggle OFF at public launch to reopen
// the app to anyone.
//
// All four endpoints are JWT-admin only (`verify_admin` accepts an admin
// JWT OR the legacy X-Admin-Password header — the frontend sends only the
// JWT). Every mutating call returns the full `{enabled, domains}` config,
// so the UI just refreshes local state from the response — no follow-up
// GET needed.
//
// • GET    /api/admin/domain-restriction          — read current config
// • POST   /api/admin/domain-restriction          — set the master toggle
// • POST   /api/admin/domain-restriction/domains  — add one domain (400 on
//          a malformed domain: must contain a dot, no `@`, no whitespace;
//          duplicate is a no-op success)
// • DELETE /api/admin/domain-restriction/domains/{domain} — remove one
//          domain (idempotent — removing an absent domain is a no-op)

export interface DomainRestrictionConfig {
  enabled: boolean;
  domains: string[];
}

export const domainRestrictionApi = {
  /** Admin only (JWT). Returns the current toggle + approved-domain list. */
  get: (): Promise<DomainRestrictionConfig> =>
    get("/api/admin/domain-restriction", authHeader()),

  /** Admin only (JWT). Flip the master toggle. Returns the full config. */
  setEnabled: (enabled: boolean): Promise<DomainRestrictionConfig> =>
    post("/api/admin/domain-restriction", { enabled }, authHeader()),

  /**
   * Admin only (JWT). Add one domain. The backend lowercases + trims and
   * validates (must contain a dot, no `@`, no whitespace); invalid → 400
   * with a clear message. Adding a duplicate is a no-op success. Returns
   * the full config.
   */
  addDomain: (domain: string): Promise<DomainRestrictionConfig> =>
    post("/api/admin/domain-restriction/domains", { domain }, authHeader()),

  /**
   * Admin only (JWT). Remove one domain. Idempotent — removing a domain
   * not present is a no-op success. Returns the full config. (The DELETE
   * endpoint returns a body, so we use `request` directly rather than the
   * void-returning `del` helper.)
   */
  removeDomain: (domain: string): Promise<DomainRestrictionConfig> =>
    request<DomainRestrictionConfig>(
      `/api/admin/domain-restriction/domains/${encodeURIComponent(domain)}`,
      { method: "DELETE" },
      authHeader(),
    ),
};

// ─── Tier-limit error parsing ─────────────────────────────────
//
// Sam (2026-05-14) — the Marie chat (POST /api/marie/chat) and execute
// (POST /api/execute) endpoints reject basic-tier requests with
// structured detail bodies. The chatStream + executeApi paths surface
// these to the UI so it can switch the model picker, lock the input,
// or toast the right CTA. Code keys are stable; `kind` distinguishes
// the two quota windows.
//
// 403 model_not_allowed:
//   { detail: { code: "model_not_allowed", message, role,
//                requested_model, allowed_models } }
// 429 quota_exceeded (marie_chat | script_run):
//   { detail: { code: "quota_exceeded", message, kind,
//                used, limit, window } }

export interface ModelNotAllowedDetail {
  code: "model_not_allowed";
  message: string;
  role: UserRole;
  requested_model: string;
  allowed_models: string[];
}

export interface QuotaExceededDetail {
  code: "quota_exceeded";
  message: string;
  kind: "marie_chat" | "script_run";
  used: number;
  limit: number;
  window: string;
}

export type TierLimitDetail = ModelNotAllowedDetail | QuotaExceededDetail;

/**
 * Error subclass thrown (or surfaced via streaming `onError`) when the
 * backend rejects a request with a structured tier-limit detail. Carries
 * `detail.message` as its `.message` so callers that only know how to
 * print a string still get the friendly copy Sam wrote. Callers that
 * want to drive UX state should narrow on `err.detail.code`.
 */
export class TierLimitError extends Error {
  readonly detail: TierLimitDetail;
  constructor(detail: TierLimitDetail) {
    super(detail.message);
    this.name = "TierLimitError";
    this.detail = detail;
  }
}

function isTierLimitDetail(value: unknown): value is TierLimitDetail {
  if (!value || typeof value !== "object") return false;
  const code = (value as { code?: unknown }).code;
  return code === "model_not_allowed" || code === "quota_exceeded";
}

/**
 * If `body.detail` is a TierLimitDetail object, return a TierLimitError;
 * otherwise return null and let the caller fall back to its existing
 * string-detail handling. Defensive — the same `detail` field is used
 * by all FastAPI HTTPException paths, so we must not break the simple
 * "detail is a string" contract that every other endpoint follows.
 */
function tierLimitErrorFromBody(body: unknown): TierLimitError | null {
  if (!body || typeof body !== "object") return null;
  const detail = (body as { detail?: unknown }).detail;
  if (isTierLimitDetail(detail)) {
    return new TierLimitError(detail);
  }
  return null;
}

// ─── Health ───────────────────────────────────────────────────
// Public, no-auth. Returns the backend's view of the live VERSION
// file so the UI can show the actual deployed version + build counter
// (rather than the static frontend package.json version which doesn't
// carry the build number).

export interface HealthStatus {
  status: string;
  service: string;
  version: string;  // e.g. "0.5.0"
  build: number;    // e.g. 11 — auto-incremented per commit by hooks/pre-commit
}

export const healthApi = {
  get: (): Promise<HealthStatus> => get("/api/health"),
};
